import { useMemo } from "react";
import type { CSSProperties, ReactNode } from "react";
import { AutoComplete, Button, Drawer, Empty, Input, theme } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import styles from "styles/layout.scss";

import AppSearchOption from "./AppSearchOption";
import { isMacPlatform, useAppSearch } from "./useAppSearch";
import type { SearchEntry, SearchOption } from "./types";

const { useToken } = theme;

interface AppSearchProps {
  compact?: boolean;
  style?: CSSProperties;
}

// Global command-palette search (Ctrl/Cmd+K). Renders inline on desktop and as a
// top drawer triggered by an icon button on compact/mobile headers. With an empty
// query it shows the user's recent picks; with a query it shows ranked results
// grouped by top-level module.
const AppSearch = ({ compact = false, style }: AppSearchProps) => {
  const { t } = useTranslation();
  const { token } = useToken();
  const {
    query,
    setQuery,
    open,
    setOpen,
    drawerOpen,
    setDrawerOpen,
    inputRef,
    results,
    recentEntries,
    navigateTo,
    focusSearch,
  } = useAppSearch({ compact });

  const options = useMemo<SearchOption[]>(() => {
    const toOption = (entry: SearchEntry): SearchOption => ({
      value: entry.id,
      item: entry,
      label: <AppSearchOption entry={entry} query={query} />,
    });

    if (!query.trim()) {
      if (recentEntries.length === 0) return [];

      return [
        {
          label: t("frontend.app_search.recent"),
          options: recentEntries.map(toOption),
        },
      ];
    }

    if (results.length === 0) {
      return [
        {
          value: "no_results",
          disabled: true,
          label: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("forms.messages.no_results")}
              className={styles.q_app_search__empty}
            />
          ),
        },
      ];
    }

    // Group ranked results under their top-level module while preserving rank
    // order: each group is anchored where its best-ranked entry first appeared,
    // so the first row in the dropdown is always the overall best match.
    const blocks: SearchOption[] = [];
    const groupBlocks = new Map<string, SearchOption>();

    results.forEach((entry) => {
      if (!entry.topGroup) {
        blocks.push(toOption(entry));
        return;
      }

      let group = groupBlocks.get(entry.topGroup);
      if (!group) {
        group = { label: entry.topGroup, options: [] };
        groupBlocks.set(entry.topGroup, group);
        blocks.push(group);
      }
      (group.options as SearchOption[]).push(toOption(entry));
    });

    return blocks;
  }, [query, results, recentEntries, t]);

  const shortcutLabel = isMacPlatform() ? "⌘ K" : "Ctrl K";

  const renderDropdown = (menu: ReactNode) => (
    <>
      {menu}
      <div className={styles.q_app_search__footer}>
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd> {t("frontend.app_search.hint_navigate")}
        </span>
        <span>
          <kbd>↵</kbd> {t("frontend.app_search.hint_open")}
        </span>
        <span>
          <kbd>Esc</kbd> {t("frontend.app_search.hint_dismiss")}
        </span>
      </div>
    </>
  );

  const className = [
    styles.q_app_search,
    compact ? "" : styles.q_app_search__inline,
    query ? styles.q_app_search__has_value : "",
  ]
    .filter(Boolean)
    .join(" ");

  const searchInput = (
    <AutoComplete
      allowClear
      className={className}
      options={options}
      value={query}
      open={open && options.length > 0}
      defaultActiveFirstOption
      onChange={setQuery}
      onSearch={(value) => {
        setQuery(value);
        setOpen(true);
      }}
      onDropdownVisibleChange={setOpen}
      onSelect={(_value, option) => {
        const selectedEntry = option.item;
        if (selectedEntry) navigateTo(selectedEntry);
      }}
      popupClassName={styles.q_app_search__dropdown}
      popupMatchSelectWidth={compact ? true : 420}
      dropdownRender={compact ? undefined : renderDropdown}
      style={compact ? { width: "100%", maxWidth: "none" } : style}
    >
      <Input
        ref={inputRef}
        variant="filled"
        prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
        suffix={
          compact ? undefined : (
            <span className={styles.q_app_search__kbd} aria-hidden="true">
              {shortcutLabel}
            </span>
          )
        }
        placeholder={t("frontend.app_search.placeholder")}
        aria-label={t("frontend.app_search.placeholder")}
        aria-keyshortcuts="Control+K Meta+K"
        onFocus={() => setOpen(true)}
      />
    </AutoComplete>
  );

  if (compact) {
    return (
      <>
        <Button
          type="text"
          icon={<SearchOutlined />}
          aria-label={t("frontend.app_search.placeholder")}
          onClick={focusSearch}
        />
        <Drawer
          placement="top"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          height={112}
          closeIcon={null}
          styles={{
            body: {
              padding: token.paddingMD,
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          {searchInput}
        </Drawer>
      </>
    );
  }

  return searchInput;
};

export default AppSearch;
